import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { Heart, Star, Sparkles, Check } from "lucide-react";
import { PriceDisplay, TransactionTypeBadge, AgeBadge } from "./Badges";
import { TrustScoreBadge } from "./TrustScoreBadge";
import { VerifiedSellerBadge } from "./VerifiedSellerBadge";
import { API } from "../lib/api";

export const SuccessPopup = ({ message, onClose }) => (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="success-popup">
    <div className="brutalist-card p-8 max-w-md w-full text-center animate-bounce-in">
      <div className="w-20 h-20 bg-[#22C55E] rounded-full flex items-center justify-center mx-auto mb-4 border-2 border-gray-900 shadow-[4px_4px_0px_0px_rgba(17,24,39,1)]">
        <Check className="w-10 h-10 text-white" />
      </div>
      <h2 className="text-2xl font-black mb-2 text-[#22C55E]" style={{ fontFamily: 'Outfit' }}>Success!</h2>
      <p className="text-gray-700 mb-6">{message}</p>
      <button onClick={onClose} className="brutalist-btn-primary" data-testid="success-close-btn">Continue</button>
    </div>
  </div>
);

export const getImageUrl = (images, fallback = 'https://images.unsplash.com/photo-1596068587619-e4b11c7a3488?w=400&h=300&fit=crop') => {
  if (!images || images.length === 0) return fallback;
  const img = images[0];
  return img.startsWith('http') ? img : `${API}/files/${img}`;
};

export const ToyCard = ({ listing, onSave, isSaved }) => {
  const imageUrl = getImageUrl(listing.images);
  const videoRef = useRef(null);
  const videoUrl = listing.video ? `${API}/files/${listing.video}` : null;

  const onEnter = () => {
    if (!videoRef.current) return;
    // Best-effort: play() can reject if the browser hasn't received user
    // interaction yet; swallow the rejection — the still image is the
    // graceful fallback.
    videoRef.current.play().catch(() => {});
  };
  const onLeave = () => {
    if (!videoRef.current) return;
    videoRef.current.pause();
    videoRef.current.currentTime = 0;
  };

  return (
    <div className="brutalist-card block relative" data-testid={`toy-card-${listing.id}`}>
      {onSave && (
        <button onClick={(e) => { e.preventDefault(); onSave(listing.id); }} className={`absolute top-3 right-3 z-10 w-8 h-8 rounded-full flex items-center justify-center border-2 border-gray-900 ${isSaved ? 'bg-[#FF6B6B] text-white' : 'bg-white'}`}>
          <Heart className={`w-4 h-4 ${isSaved ? 'fill-white' : ''}`} />
        </button>
      )}
      <Link to={`/listing/${listing.id}`}>
        <div
          className="group relative aspect-square overflow-hidden"
          onMouseEnter={videoUrl ? onEnter : undefined}
          onMouseLeave={videoUrl ? onLeave : undefined}
        >
          <img src={imageUrl} alt={listing.title} className="w-full h-full object-cover" onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1596068587619-e4b11c7a3488?w=400&h=300&fit=crop'; }} />
          {videoUrl && (
            <video
              ref={videoRef}
              src={videoUrl}
              muted
              loop
              playsInline
              preload="metadata"
              className="absolute inset-0 w-full h-full object-cover opacity-0 group-hover:opacity-100 transition-opacity duration-200"
              data-testid={`toy-card-video-preview-${listing.id}`}
            />
          )}
          <div className="absolute top-3 left-3 flex flex-col gap-2 z-[1]">
            <TransactionTypeBadge type={listing.transaction_type} />
            {listing.is_promoted && <span className="bg-[#FFD93D] text-gray-900 text-xs px-2 py-1 rounded-full font-bold flex items-center gap-1"><Sparkles className="w-3 h-3" />Featured</span>}
            {listing.video && <span className="bg-gray-900 text-white text-xs px-2 py-1 rounded-full font-bold flex items-center gap-1" data-testid={`toy-card-video-${listing.id}`}><svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>VIDEO</span>}
          </div>
          <div className="absolute bottom-3 left-3 z-[1]"><AgeBadge ageGroup={listing.age_group} /></div>
        </div>
        <div className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <span className="brutalist-badge text-[10px]">{listing.condition}</span>
            <span className="text-gray-500 text-xs">{listing.category}</span>
          </div>
          <h3 className="font-bold text-lg mb-1 line-clamp-1" style={{ fontFamily: 'Outfit' }}>{listing.title}</h3>
          <p className="text-gray-600 text-sm mb-2 line-clamp-2">{listing.description}</p>
          <div className="flex items-center justify-between">
            <PriceDisplay price={listing.price} isDonation={listing.is_donation || listing.transaction_type === 'donate'} className="font-black text-xl text-[#FF6B6B]" />
            <div className="flex items-center gap-2">
              <VerifiedSellerBadge verified={listing.seller_verified} />
              {listing.seller_rating > 0 && <div className="flex items-center gap-1 text-sm"><Star className="w-4 h-4 fill-[#FFD93D] text-[#FFD93D]" /><span>{listing.seller_rating.toFixed(1)}</span></div>}
              <TrustScoreBadge userId={listing.seller_id} compact />
            </div>
          </div>
        </div>
      </Link>
    </div>
  );
};
